import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function GameEditorDemo() {
  // Loading and saving state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState(null);
  // Taxi state
  const [taxiPos, setTaxiPos] = useState({ x: 500, y: 400, z: 0 });
  const [taxiSize, setTaxiSize] = useState(60);
  const [taxiRotation, setTaxiRotation] = useState(0); // 0-360 degrees
  const [taxiRotationOffset, setTaxiRotationOffset] = useState(0); // Offset for auto-rotation
  const [taxiFlipH, setTaxiFlipH] = useState(false); // Flip horizontal
  const [taxiFlipV, setTaxiFlipV] = useState(false); // Flip vertical
  const [taxiStyle, setTaxiStyle] = useState('classic'); // classic, modern, van
  const [isDraggingTaxi, setIsDraggingTaxi] = useState(false);
  
  // Characters management system
  const [characters, setCharacters] = useState([
    {
      id: 'char1',
      name: 'Character 1',
      position: { x: 300, y: 300, z: 0 },
      size: 80,
      customImage: null,
      emoji: '🧕',
      conversations: [] // Array of conversation nodes
    }
  ]);
  const [selectedCharacterId, setSelectedCharacterId] = useState('char1');
  const [isDraggingCharacter, setIsDraggingCharacter] = useState(null); // ID of character being dragged
  const [newCharacterName, setNewCharacterName] = useState('');
  
  // Conversation system
  const [activeConversationCharId, setActiveConversationCharId] = useState(null); // Which character's conversation we're building
  const [userGender, setUserGender] = useState('male'); // 'male' or 'female'
  const [userAvatar, setUserAvatar] = useState('👨'); // User's avatar emoji
  const [editingNodeId, setEditingNodeId] = useState(null); // Which node we're editing
  const [showPreview, setShowPreview] = useState(false); // Show conversation preview
  const [previewCurrentNodeId, setPreviewCurrentNodeId] = useState(null); // Current node in preview
  const [showAvatarSelection, setShowAvatarSelection] = useState(false); // Show avatar selection screen in preview
  
  // User avatar based on gender
  React.useEffect(() => {
    setUserAvatar(userGender === 'male' ? '👨' : '👩');
  }, [userGender]);
  
  // Active tab in right panel
  const [rightPanelTab, setRightPanelTab] = useState('elements'); // 'elements', 'conversations', 'settings'
  
  // Markers (waypoints)
  const [markers, setMarkers] = useState([]);
  const [markerName, setMarkerName] = useState('');
  const [showMarkersOnCanvas, setShowMarkersOnCanvas] = useState(true);
  
  // Load config from database on mount
  useEffect(() => {
    loadGameConfig();
  }, []);
  
  const loadGameConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/game/config`);
      const config = response.data;
      
      console.log('📦 Loaded config:', config);
      
      // Load background if exists
      if (config.background_url) {
        setBgImageUrl(config.background_url);
      }
      
      // Load characters if exists
      if (config.characters && config.characters.length > 0) {
        console.log('👥 Loading characters:', config.characters);
        // Restore characters with conversations
        const loadedChars = config.characters.map(char => ({
          ...char,
          customImage: null // Reset image object
        }));
        setCharacters(loadedChars);
        setSelectedCharacterId(loadedChars[0].id);
      }
      
      // Load hidden gems if exists
      if (config.hidden_gems && config.hidden_gems.length > 0) {
        setHiddenGems(config.hidden_gems);
        console.log('💎 Loaded hidden gems:', config.hidden_gems);
      }
      
      // Load game settings
      if (config.starting_gems) setStartingGems(config.starting_gems);
      if (config.requires_membership !== undefined) setRequiresMembership(config.requires_membership);
      if (config.game_price) setGamePrice(config.game_price);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading config:', error);
      setIsLoading(false);
    }
  };
  
  const saveGameConfig = async () => {
    setIsSaving(true);
    try {
      // Serialize characters properly
      const serializedCharacters = characters.map(char => {
        const serialized = {
          id: char.id,
          name: char.name,
          position: {
            x: Math.round(char.position.x),
            y: Math.round(char.position.y),
            z: char.position.z || 0
          },
          size: char.size,
          emoji: char.emoji,
          conversations: [],
          is_translator: char.is_translator || false
        };
        
        // Serialize conversations
        if (char.conversations && Array.isArray(char.conversations)) {
          serialized.conversations = char.conversations.map(node => ({
            id: node.id,
            text: node.text,
            isStart: node.isStart || false,
            options: (node.options || []).map(opt => ({
              id: opt.id,
              text: opt.text,
              nextNodeId: opt.nextNodeId || null
            }))
          }));
        }
        
        return serialized;
      });
      
      // Prepare config object
      const config = {
        background_url: bgImageUrl,
        characters: serializedCharacters,
        hidden_gems: hiddenGems || [],
        locations: [
          { id: 'main', name: 'Main Scene', background_url: bgImageUrl, description: null }
        ],
        store_items: [
          { id: 'food1', name: 'Moroccan Bread', price: 50, type: 'food', energy_restore: 20, description: 'Fresh bread' },
          { id: 'food2', name: 'Tagine', price: 150, type: 'food', energy_restore: 50, description: 'Traditional dish' }
        ],
        translator_character_id: serializedCharacters.find(c => c.is_translator)?.id || null,
        taxi_cost: 200,
        hotel_cost: 500,
        starting_gems: startingGems || 1000,
        requires_membership: requiresMembership || false,
        game_price: gamePrice || 0
      };
      
      console.log('Saving config:', config);
      
      await axios.post(`${API_URL}/api/game/config`, config);
      
      setSaveMessage('✅ Configuration saved!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setSaveMessage('❌ Failed to save: ' + (error.response?.data?.detail || error.message));
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Background state
  const [bgZoom, setBgZoom] = useState(1);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  
  // Custom images
  const [customBgImage, setCustomBgImage] = useState(null);
  const [customTaxiImage, setCustomTaxiImage] = useState(null);
  const [bgImageUrl, setBgImageUrl] = useState('/assets/game/cityscape.png');
  const [showHotelOverlay, setShowHotelOverlay] = useState(false);
  
  // Game Settings
  const [hiddenGems, setHiddenGems] = useState([]);
  const [startingGems, setStartingGems] = useState(1000);
  const [requiresMembership, setRequiresMembership] = useState(false);
  const [gamePrice, setGamePrice] = useState(0);
  const [placingGem, setPlacingGem] = useState(null); // 'ruby', 'emerald', 'diamond', or null
  
  // Active element
  const [activeElement, setActiveElement] = useState('taxi'); // 'taxi' or 'character'
  
  // Animation
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(0.01); // 0.005 (slow) to 0.02 (fast)
  const animationRef = useRef(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / bgZoom;
    const y = (e.clientY - rect.top) / bgZoom;
    
    if (isDraggingTaxi) {
      setTaxiPos({ x, y });
    }
    if (isDraggingCharacter) {
      setCharacters(prev => prev.map(char => 
        char.id === isDraggingCharacter 
          ? { ...char, position: { ...char.position, x, y } }
          : char
      ));
    }
  };

  const handleMouseUp = () => {
    setIsDraggingTaxi(false);
    setIsDraggingCharacter(null);
  };

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Load background image
    const bgImg = customBgImage || new Image();
    if (!customBgImage) {
      bgImg.src = bgImageUrl;
    }
    
    const hotelImg = new Image();
    hotelImg.src = '/assets/game/hotel.png';
    
    let imagesLoaded = 0;
    const checkLoaded = () => {
      imagesLoaded++;
      if (imagesLoaded === 2) draw();
    };
    
    if (customBgImage) {
      checkLoaded(); // Already loaded
    } else {
      bgImg.onload = checkLoaded;
    }
    hotelImg.onload = checkLoaded;
    
    const draw = () => {
      ctx.clearRect(0, 0, 1200, 800);
      
      // Save context for zoom
      ctx.save();
      ctx.scale(bgZoom, bgZoom);
      ctx.translate(bgPosition.x, bgPosition.y);
      
      // Draw background
      if (bgImg && bgImg.complete && bgImg.naturalHeight !== 0) {
        try {
          ctx.drawImage(bgImg, 0, 0, 1200, 800);
        } catch (e) {
          console.error('Failed to draw background:', e);
        }
      }
      
      // Draw hotel (only if enabled)
      if (showHotelOverlay && hotelImg && hotelImg.complete && hotelImg.naturalHeight !== 0) {
        try {
          ctx.drawImage(hotelImg, 200, 280, 200, 200);
        } catch (e) {
          console.error('Failed to draw hotel:', e);
        }
      }
      
      // Draw grid lines for reference
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 1200; i += 100) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, 800);
        ctx.stroke();
      }
      for (let i = 0; i < 800; i += 100) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(1200, i);
        ctx.stroke();
      }
      
      // Draw path connecting markers (only if visible)
      if (showMarkersOnCanvas && markers.length > 1) {
        ctx.strokeStyle = '#FF6B6B';
        ctx.lineWidth = 4;
        ctx.setLineDash([10, 10]);
        ctx.beginPath();
        ctx.moveTo(markers[0].position.x, markers[0].position.y - markers[0].position.z);
        for (let i = 1; i < markers.length; i++) {
          const displayY = markers[i].position.y - markers[i].position.z;
          ctx.lineTo(markers[i].position.x, displayY);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
      
      // Draw all saved markers (only if visible)
      if (showMarkersOnCanvas) {
        markers.forEach((marker, index) => {
          const displayY = marker.position.y - marker.position.z;
          
          // Marker circle
          ctx.fillStyle = marker.element === 'taxi' ? '#FFD700' : '#00FF00';
          ctx.beginPath();
          ctx.arc(marker.position.x, displayY, 15, 0, Math.PI * 2);
          ctx.fill();
          
          // Marker number
          ctx.fillStyle = '#000';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(index + 1, marker.position.x, displayY);
          
          // Marker label
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(marker.position.x - 40, displayY - 35, 80, 20);
          ctx.strokeStyle = marker.element === 'taxi' ? '#FFD700' : '#00FF00';
          ctx.lineWidth = 2;
          ctx.strokeRect(marker.position.x - 40, displayY - 35, 80, 20);
          
          ctx.fillStyle = '#000';
          ctx.font = '12px Arial';
          ctx.fillText(marker.name, marker.position.x, displayY - 25);
          
          // Note indicator if marker has note
          if (marker.note) {
            ctx.fillStyle = '#2196F3';
            ctx.beginPath();
            ctx.arc(marker.position.x + 12, displayY - 12, 6, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#FFF';
            ctx.font = 'bold 10px Arial';
            ctx.fillText('💬', marker.position.x + 12, displayY - 9);
          }
        });
      }
      
      // Show speech bubble if at a marker with a note
      if (showNoteAtMarker && showNoteAtMarker.note) {
        const notePos = showNoteAtMarker.position;
        const noteY = notePos.y - notePos.z;
        
        // Speech bubble
        const bubbleWidth = Math.min(300, showNoteAtMarker.note.length * 8 + 40);
        const bubbleHeight = 60;
        const bubbleX = notePos.x;
        const bubbleY = noteY - 80;
        
        // Bubble background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.strokeStyle = '#2196F3';
        ctx.lineWidth = 3;
        roundRect(ctx, bubbleX - bubbleWidth/2, bubbleY - bubbleHeight/2, bubbleWidth, bubbleHeight, 15);
        ctx.fill();
        ctx.stroke();
        
        // Bubble pointer
        ctx.beginPath();
        ctx.moveTo(bubbleX - 10, bubbleY + bubbleHeight/2);
        ctx.lineTo(bubbleX, noteY - 20);
        ctx.lineTo(bubbleX + 10, bubbleY + bubbleHeight/2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Text
        ctx.fillStyle = '#000';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        wrapText(ctx, showNoteAtMarker.note, bubbleX, bubbleY, bubbleWidth - 20, 18);
      }
      
      // Draw hidden gems
      hiddenGems.forEach((gem) => {
        const gemEmoji = gem.type === 'diamond' ? '💎' : gem.type === 'emerald' ? '💚' : '❤️';
        ctx.font = '32px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(gemEmoji, gem.x, gem.y);
        
        // If in gem placing mode, show value
        if (activeElement === 'gems') {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.fillRect(gem.x - 30, gem.y - 45, 60, 20);
          ctx.strokeStyle = gem.type === 'diamond' ? '#2196F3' : gem.type === 'emerald' ? '#4CAF50' : '#F44336';
          ctx.lineWidth = 2;
          ctx.strokeRect(gem.x - 30, gem.y - 45, 60, 20);
          ctx.fillStyle = '#000';
          ctx.font = '11px Arial';
          ctx.fillText(`${gem.value} gems`, gem.x, gem.y - 35);
        }
      });
      
      // Draw all Characters (Z-index determines render order)
      characters.forEach(character => {
        ctx.font = `${character.size}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Character (Y position adjusted by Z for isometric)
        const charDisplayY = character.position.y - character.position.z;
        
        if (character.customImage && character.customImage.complete && character.customImage.naturalHeight !== 0) {
          // Draw custom character image (only if loaded successfully)
          try {
            ctx.drawImage(character.customImage, character.position.x - character.size/2, charDisplayY - character.size/2, character.size, character.size);
          } catch (e) {
            // If image fails, fallback to emoji
            ctx.fillText(character.emoji, character.position.x, charDisplayY);
          }
        } else {
          // Draw emoji
          ctx.fillText(character.emoji, character.position.x, charDisplayY);
        }
        
        // Character border (if selected or dragging)
        if (character.id === selectedCharacterId || isDraggingCharacter === character.id) {
          ctx.strokeStyle = character.id === selectedCharacterId ? '#4CAF50' : '#00FF00';
          ctx.lineWidth = 3;
          ctx.strokeRect(
            character.position.x - character.size/2, 
            charDisplayY - character.size/2, 
            character.size, 
            character.size
          );
        }
        
        // Character name label
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillRect(character.position.x - 40, charDisplayY - character.size/2 - 25, 80, 20);
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 2;
        ctx.strokeRect(character.position.x - 40, charDisplayY - character.size/2 - 25, 80, 20);
        ctx.fillStyle = '#000';
        ctx.font = 'bold 11px Arial';
        ctx.fillText(character.name, character.position.x, charDisplayY - character.size/2 - 15);
        
        // Z-level indicator for character
        if (character.position.z !== 0) {
          ctx.fillStyle = 'rgba(76, 175, 80, 0.3)';
          ctx.fillRect(character.position.x - 2, charDisplayY, 4, character.position.z);
          ctx.fillStyle = '#4CAF50';
          ctx.font = 'bold 12px Arial';
          ctx.fillText(`Z:${character.position.z}`, character.position.x, character.position.y + character.size * 0.6);
        }
      });
      
      // Draw Taxi (Y position adjusted by Z)
      const taxiDisplayY = taxiPos.y - taxiPos.z;
      
      if (customTaxiImage && customTaxiImage.complete && customTaxiImage.naturalHeight !== 0) {
        // Draw custom taxi image
        try {
          ctx.save();
          ctx.translate(taxiPos.x, taxiDisplayY);
          ctx.rotate((taxiRotation * Math.PI) / 180);
          const scaleX = taxiFlipH ? -1 : 1;
          const scaleY = taxiFlipV ? -1 : 1;
          ctx.scale(scaleX, scaleY);
          ctx.drawImage(customTaxiImage, -taxiSize/2, -taxiSize/2, taxiSize, taxiSize);
          ctx.restore();
        } catch (e) {
          console.error('Failed to draw custom taxi:', e);
          // Fallback to default taxi
          drawTaxi(ctx, taxiPos.x, taxiDisplayY, taxiSize, taxiRotation, taxiStyle, taxiFlipH, taxiFlipV);
        }
      } else {
        drawTaxi(ctx, taxiPos.x, taxiDisplayY, taxiSize, taxiRotation, taxiStyle, taxiFlipH, taxiFlipV);
      }
      
      // Taxi border (if dragging)
      if (isDraggingTaxi) {
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 3;
        ctx.strokeRect(
          taxiPos.x - taxiSize/2, 
          taxiDisplayY - taxiSize/2, 
          taxiSize, 
          taxiSize
        );
      }
      
      // Z-level indicator for taxi
      if (taxiPos.z !== 0) {
        ctx.fillStyle = 'rgba(255, 215, 0, 0.3)';
        ctx.fillRect(taxiPos.x - 2, taxiDisplayY, 4, taxiPos.z);
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 12px Arial';
        ctx.fillText(`Z:${taxiPos.z}`, taxiPos.x, taxiPos.y + taxiSize * 0.6);
      }
      
      ctx.restore();
    };
    
    const animationLoop = () => {
      draw();
      requestAnimationFrame(animationLoop);
    };
    
    animationLoop();
  }, [taxiPos, taxiSize, characters, isDraggingTaxi, isDraggingCharacter, bgZoom, bgPosition, bgImageUrl, customBgImage, markers, taxiFlipH, taxiFlipV, taxiRotation, taxiStyle, showMarkersOnCanvas, selectedCharacterId, hiddenGems, activeElement]);

  // Helper functions for drawing
  const roundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
    const words = text.split(' ');
    let line = '';
    let lineY = y - 10;
    
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && n > 0) {
        ctx.fillText(line, x, lineY);
        line = words[n] + ' ';
        lineY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, lineY);
  };

  const drawTaxi = (ctx, x, y, size, rotation = 0, style = 'classic', flipH = false, flipV = false) => {
    const scale = size / 60;
    
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((rotation * Math.PI) / 180);
    
    // Apply flips
    const scaleX = flipH ? -scale : scale;
    const scaleY = flipV ? -scale : scale;
    ctx.scale(scaleX, scaleY);
    
    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(0, 22, 30, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    
    if (style === 'classic') {
      // Classic Taxi (Yellow Cab)
      // Body - isometric perspective
      ctx.fillStyle = '#FFD700';
      ctx.strokeStyle = '#DAA520';
      ctx.lineWidth = 2;
      
      // Main body (isometric box)
      ctx.beginPath();
      ctx.moveTo(-30, -10);
      ctx.lineTo(-30, 10);
      ctx.lineTo(30, 10);
      ctx.lineTo(30, -10);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Top part (roof)
      ctx.fillStyle = '#DAA520';
      ctx.beginPath();
      ctx.moveTo(-20, -18);
      ctx.lineTo(-20, -10);
      ctx.lineTo(20, -10);
      ctx.lineTo(20, -18);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Taxi sign on roof
      ctx.fillStyle = '#FF0000';
      ctx.fillRect(-12, -25, 24, 7);
      ctx.strokeRect(-12, -25, 24, 7);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 8px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('TAXI', 0, -20);
      
      // Windows (darker blue)
      ctx.fillStyle = '#4682B4';
      ctx.fillRect(-18, -16, 14, 10);
      ctx.fillRect(4, -16, 14, 10);
      
      // Window shine
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.fillRect(-16, -15, 6, 5);
      ctx.fillRect(6, -15, 6, 5);
      
      // Wheels (isometric)
      ctx.fillStyle = '#222';
      ctx.beginPath();
      ctx.ellipse(-20, 12, 8, 4, 0, 0, Math.PI * 2);
      ctx.ellipse(20, 12, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Wheel hubs
      ctx.fillStyle = '#666';
      ctx.beginPath();
      ctx.ellipse(-20, 12, 4, 2, 0, 0, Math.PI * 2);
      ctx.ellipse(20, 12, 4, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Headlights
      ctx.fillStyle = '#FFFF99';
      ctx.fillRect(28, 2, 3, 4);
      ctx.fillRect(28, -6, 3, 4);
      
    } else if (style === 'modern') {
      // Modern sleek taxi
      ctx.fillStyle = '#00BCD4';
      
      // Streamlined body
      ctx.beginPath();
      ctx.moveTo(-32, 0);
      ctx.quadraticCurveTo(-30, -15, -15, -18);
      ctx.lineTo(15, -18);
      ctx.quadraticCurveTo(30, -15, 32, 0);
      ctx.lineTo(32, 12);
      ctx.lineTo(-32, 12);
      ctx.closePath();
      ctx.fill();
      
      ctx.strokeStyle = '#0097A7';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Windows
      ctx.fillStyle = '#263238';
      ctx.beginPath();
      ctx.moveTo(-22, -8);
      ctx.lineTo(-22, -14);
      ctx.lineTo(-10, -15);
      ctx.lineTo(-10, -8);
      ctx.closePath();
      ctx.fill();
      
      ctx.beginPath();
      ctx.moveTo(10, -8);
      ctx.lineTo(10, -15);
      ctx.lineTo(22, -14);
      ctx.lineTo(22, -8);
      ctx.closePath();
      ctx.fill();
      
      // Modern wheels
      ctx.fillStyle = '#222';
      ctx.beginPath();
      ctx.ellipse(-22, 12, 9, 5, 0, 0, Math.PI * 2);
      ctx.ellipse(22, 12, 9, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      
    } else if (style === 'van') {
      // Box-style van
      ctx.fillStyle = '#FF6B6B';
      
      // Box body
      ctx.fillRect(-28, -25, 56, 35);
      ctx.strokeStyle = '#D84315';
      ctx.lineWidth = 2;
      ctx.strokeRect(-28, -25, 56, 35);
      
      // Front windshield
      ctx.fillStyle = '#4682B4';
      ctx.beginPath();
      ctx.moveTo(20, -22);
      ctx.lineTo(26, -18);
      ctx.lineTo(26, -8);
      ctx.lineTo(20, -8);
      ctx.closePath();
      ctx.fill();
      
      // Side windows
      ctx.fillRect(-20, -20, 12, 12);
      ctx.fillRect(-5, -20, 12, 12);
      ctx.fillRect(10, -20, 8, 12);
      
      // Wheels
      ctx.fillStyle = '#222';
      ctx.beginPath();
      ctx.ellipse(-20, 12, 8, 4, 0, 0, Math.PI * 2);
      ctx.ellipse(15, 12, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    
    ctx.restore();
  };

  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / bgZoom;
    const y = (e.clientY - rect.top) / bgZoom;
    
    // Gem placement mode
    if (activeElement === 'gems' && placingGem) {
      const gemValue = placingGem === 'diamond' ? 300 : placingGem === 'emerald' ? 150 : 100;
      const newGem = {
        id: `gem_${Date.now()}`,
        x: Math.round(x),
        y: Math.round(y),
        type: placingGem,
        scene: 'main',
        value: gemValue
      };
      setHiddenGems([...hiddenGems, newGem]);
      // Don't reset placingGem so user can place multiple
      e.preventDefault();
      return;
    }
    
    // Check if click is on taxi (adjusted for Z-axis display)
    const taxiDisplayY = taxiPos.y - taxiPos.z;
    if (activeElement === 'taxi' && 
        Math.abs(x - taxiPos.x) < taxiSize/2 && 
        Math.abs(y - taxiDisplayY) < taxiSize/2) {
      setIsDraggingTaxi(true);
      e.preventDefault();
      return;
    }
    
    // Check if click is on any character (adjusted for Z-axis display)
    if (activeElement === 'character') {
      for (const character of characters) {
        const charDisplayY = character.position.y - character.position.z;
        if (Math.abs(x - character.position.x) < character.size/2 && 
            Math.abs(y - charDisplayY) < character.size/2) {
          setIsDraggingCharacter(character.id);
          setSelectedCharacterId(character.id);
          e.preventDefault();
          return;
        }
      }
    }
  };

  const [markerNote, setMarkerNote] = useState('');
  const [showNoteAtMarker, setShowNoteAtMarker] = useState(null);

  const [useAutoRotation, setUseAutoRotation] = useState(false); // Toggle auto-rotation

  // Character Management Functions
  const getSelectedCharacter = () => {
    return characters.find(c => c.id === selectedCharacterId);
  };

  const updateSelectedCharacter = (updates) => {
    setCharacters(prev => prev.map(char =>
      char.id === selectedCharacterId
        ? { ...char, ...updates }
        : char
    ));
  };

  const addNewCharacter = () => {
    const name = newCharacterName.trim() || `Character ${characters.length + 1}`;
    const newChar = {
      id: `char${Date.now()}`,
      name: name,
      position: { x: 400, y: 400, z: 0 },
      size: 80,
      customImage: null,
      emoji: '👤',
      conversations: []
    };
    setCharacters([...characters, newChar]);
    setSelectedCharacterId(newChar.id);
    setNewCharacterName('');
  };

  const deleteCharacter = (id) => {
    if (characters.length <= 1) {
      alert('Cannot delete the last character!');
      return;
    }
    setCharacters(prev => prev.filter(c => c.id !== id));
    if (selectedCharacterId === id) {
      setSelectedCharacterId(characters.find(c => c.id !== id).id);
    }
  };

  const handleCharacterImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          updateSelectedCharacter({ customImage: img });
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // Conversation Management
  const getCharacterById = (id) => {
    return characters.find(c => c.id === id);
  };

  const addConversationNode = (characterId, nodeText, isStart = false) => {
    const newNode = {
      id: `node${Date.now()}`,
      text: nodeText,
      options: [], // Array of {id, text, nextNodeId}
      isStart: isStart
    };
    
    setCharacters(prev => prev.map(char =>
      char.id === characterId
        ? {
            ...char,
            conversations: [...char.conversations, newNode]
          }
        : char
    ));
    
    return newNode.id;
  };

  const updateConversationNode = (characterId, nodeId, updates) => {
    setCharacters(prev => prev.map(char =>
      char.id === characterId
        ? {
            ...char,
            conversations: char.conversations.map(node =>
              node.id === nodeId ? { ...node, ...updates } : node
            )
          }
        : char
    ));
  };

  const deleteConversationNode = (characterId, nodeId) => {
    setCharacters(prev => prev.map(char =>
      char.id === characterId
        ? {
            ...char,
            conversations: char.conversations.filter(node => node.id !== nodeId)
          }
        : char
    ));
  };

  const addResponseOption = (characterId, nodeId, optionText, nextNodeId = null) => {
    const character = getCharacterById(characterId);
    const node = character.conversations.find(n => n.id === nodeId);
    
    const newOption = {
      id: `opt${Date.now()}`,
      text: optionText,
      nextNodeId: nextNodeId
    };
    
    updateConversationNode(characterId, nodeId, {
      options: [...(node.options || []), newOption]
    });
  };

  const deleteResponseOption = (characterId, nodeId, optionId) => {
    const character = getCharacterById(characterId);
    const node = character.conversations.find(n => n.id === nodeId);
    
    updateConversationNode(characterId, nodeId, {
      options: node.options.filter(opt => opt.id !== optionId)
    });
  };

  const updateResponseOption = (characterId, nodeId, optionId, updates) => {
    const character = getCharacterById(characterId);
    const node = character.conversations.find(n => n.id === nodeId);
    
    updateConversationNode(characterId, nodeId, {
      options: node.options.map(opt => 
        opt.id === optionId ? { ...opt, ...updates } : opt
      )
    });
  };

  const setStartNode = (characterId, nodeId) => {
    setCharacters(prev => prev.map(char =>
      char.id === characterId
        ? {
            ...char,
            conversations: char.conversations.map(node => ({
              ...node,
              isStart: node.id === nodeId
            }))
          }
        : char
    ));
  };

  const startPreview = (characterId) => {
    const character = getCharacterById(characterId);
    if (!character.conversations || character.conversations.length === 0) {
      // Don't show alert, just return
      return;
    }
    // Show avatar selection first
    setShowPreview(true);
    setShowAvatarSelection(true);
    setPreviewCurrentNodeId(null);
  };

  const startConversationAfterAvatarSelection = () => {
    const character = getCharacterById(activeConversationCharId);
    const startNode = character.conversations.find(n => n.isStart);
    if (startNode) {
      setPreviewCurrentNodeId(startNode.id);
    } else if (character.conversations.length > 0) {
      setPreviewCurrentNodeId(character.conversations[0].id);
    }
    setShowAvatarSelection(false);
  };

  const addMarker = () => {
    const name = markerName.trim() || `Marker ${markers.length + 1}`;
    const selectedChar = getSelectedCharacter();
    const newMarker = {
      id: Date.now(),
      name: name,
      note: markerNote.trim(), // Add custom text note
      element: activeElement,
      position: activeElement === 'taxi' ? {...taxiPos} : {...selectedChar.position},
      size: activeElement === 'taxi' ? taxiSize : selectedChar.size,
      rotation: activeElement === 'taxi' ? taxiRotation : 0, // SAVE current rotation
      flipH: activeElement === 'taxi' ? taxiFlipH : false,
      flipV: activeElement === 'taxi' ? taxiFlipV : false,
    };
    setMarkers([...markers, newMarker]);
    setMarkerName('');
    setMarkerNote('');
  };

  const deleteMarker = (id) => {
    setMarkers(markers.filter(m => m.id !== id));
  };

  const goToMarker = (marker) => {
    if (marker.element === 'taxi') {
      setTaxiPos(marker.position);
      setTaxiSize(marker.size);
      setTaxiRotation(marker.rotation || 0); // Restore saved rotation
      setTaxiFlipH(marker.flipH || false);
      setTaxiFlipV(marker.flipV || false);
      setActiveElement('taxi');
    } else {
      // Move the selected character to marker position
      updateSelectedCharacter({
        position: marker.position,
        size: marker.size
      });
      setActiveElement('character');
    }
  };

  const copyAllMarkers = () => {
    const markersText = markers.map((m, i) => 
      `${i + 1}. ${m.name} (${m.element}): { x: ${m.position.x}, y: ${m.position.y}, z: ${m.position.z}, size: ${m.size} }`
    ).join('\n');
    navigator.clipboard.writeText(markersText);
    alert('All markers copied to clipboard!');
  };

  const exportPath = () => {
    const path = markers.map(m => ({
      name: m.name,
      x: m.position.x,
      y: m.position.y,
      z: m.position.z,
    }));
    const json = JSON.stringify(path, null, 2);
    navigator.clipboard.writeText(json);
    alert('Path exported as JSON to clipboard!');
  };

  const playAnimation = () => {
    if (markers.length < 2) {
      alert('Add at least 2 markers to play animation!');
      return;
    }
    
    setIsPlaying(true);
    setCurrentMarkerIndex(0);
    
    // Move to first marker
    const firstMarker = markers[0];
    if (firstMarker.element === 'taxi') {
      setTaxiPos(firstMarker.position);
      setActiveElement('taxi');
    }
    
    animateToNextMarker(0);
  };

  const animateToNextMarker = (index) => {
    if (index >= markers.length - 1) {
      setIsPlaying(false);
      return;
    }

    const currentMarker = markers[index];
    const nextMarker = markers[index + 1];
    
    const startPos = currentMarker.position;
    const endPos = nextMarker.position;
    
    let progress = 0;
    const speed = 0.01; // Adjust speed (lower = slower)
    
    const animate = () => {
      progress += animationSpeed;
      
      if (progress >= 1) {
        // Reached next marker
        setCurrentMarkerIndex(index + 1);
        
        // Show note if marker has one
        if (nextMarker.note) {
          setShowNoteAtMarker(nextMarker);
        }
        
        // Wait longer if there's a note to show
        const waitTime = nextMarker.note ? 3000 : 500;
        setTimeout(() => {
          setShowNoteAtMarker(null);
          animateToNextMarker(index + 1);
        }, waitTime);
        return;
      }
      
      // Interpolate position
      const newPos = {
        x: startPos.x + (endPos.x - startPos.x) * progress,
        y: startPos.y + (endPos.y - startPos.y) * progress,
        z: startPos.z + (endPos.z - startPos.z) * progress,
      };
      
      // Determine rotation
      let angle;
      if (useAutoRotation) {
        // Auto-calculate rotation based on movement direction
        const dx = endPos.x - startPos.x;
        const dy = endPos.y - startPos.y;
        angle = Math.atan2(dy, dx) * (180 / Math.PI);
        angle = angle + taxiRotationOffset; // Apply offset
      } else {
        // Use saved rotations from markers - interpolate between them
        const startRotation = currentMarker.rotation || 0;
        const endRotation = nextMarker.rotation || 0;
        angle = startRotation + (endRotation - startRotation) * progress;
      }
      
      if (currentMarker.element === 'taxi') {
        setTaxiPos(newPos);
        setTaxiRotation(angle);
        // Also interpolate flip states if needed
        setTaxiFlipH(nextMarker.flipH || false);
        setTaxiFlipV(nextMarker.flipV || false);
      } else {
        updateSelectedCharacter({ position: newPos });
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };

  const stopAnimation = () => {
    setIsPlaying(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const resetToStart = () => {
    stopAnimation();
    if (markers.length > 0) {
      const firstMarker = markers[0];
      if (firstMarker.element === 'taxi') {
        setTaxiPos(firstMarker.position);
        setActiveElement('taxi');
      } else {
        updateSelectedCharacter({ position: firstMarker.position });
        setActiveElement('character');
      }
      setCurrentMarkerIndex(0);
    }
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setCustomBgImage(img);
          setBgImageUrl(event.target.result);
          setShowHotelOverlay(false); // Auto-hide hotel when custom background uploaded
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTaxiImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setCustomTaxiImage(img);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const resetToDefaults = () => {
    setCustomBgImage(null);
    setCustomTaxiImage(null);
    // Reset all character images
    setCharacters(prev => prev.map(char => ({
      ...char,
      customImage: null
    })));
    setBgImageUrl('/assets/game/cityscape.png');
    setShowHotelOverlay(true); // Show hotel again when resetting
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🎮 Game Element Position Editor</h1>
        
        {/* Character Interaction Buttons */}
        <div style={styles.characterInteractionBar}>
          <span style={{ fontSize: '14px', fontWeight: 'bold', marginRight: '10px' }}>
            💬 Talk to:
          </span>
          {characters.map(char => (
            <button
              key={char.id}
              onClick={() => {
                setActiveConversationCharId(char.id);
                setRightPanelTab('conversations');
              }}
              style={{
                ...styles.charInteractionBtn,
                background: activeConversationCharId === char.id 
                  ? 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              {char.customImage ? '🖼️' : char.emoji} {char.name}
            </button>
          ))}
        </div>
        
        <button 
          onClick={() => setShowMarkersOnCanvas(!showMarkersOnCanvas)}
          style={{
            ...styles.toggleMarkersBtn,
            ...(showMarkersOnCanvas ? styles.toggleMarkersBtnVisible : styles.toggleMarkersBtnHidden)
          }}
        >
          {showMarkersOnCanvas ? '👁️ Markers Visible' : '👁️‍🗨️ Markers Hidden'}
        </button>
      </div>
      
      <div style={styles.mainContent}>
        {/* Canvas Area */}
        <div style={styles.canvasArea}>
          <canvas
            ref={canvasRef}
            width={1200}
            height={800}
            style={styles.canvas}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />

          {/* Conversation Overlay ON CANVAS */}
          {showPreview && activeConversationCharId && (
            <div style={styles.canvasOverlay}>
              {/* Avatar Selection Screen */}
              {showAvatarSelection && (
                <div style={styles.overlayContent}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎭</div>
                  <h2 style={{ fontSize: '28px', marginBottom: '15px', fontWeight: 'bold', color: 'white' }}>
                    Choose Your Character
                  </h2>
                  <p style={{ fontSize: '16px', marginBottom: '30px', color: 'white', opacity: 0.9 }}>
                    Select your avatar to continue
                  </p>

                  <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px' }}>
                    <button
                      onClick={() => setUserGender('male')}
                      style={{
                        ...styles.canvasAvatarCard,
                        border: userGender === 'male' ? '4px solid #4CAF50' : '4px solid rgba(255,255,255,0.5)',
                      }}
                    >
                      <div style={{ fontSize: '80px' }}>👨</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '10px' }}>Male</div>
                      {userGender === 'male' && <div style={styles.checkmark}>✓</div>}
                    </button>

                    <button
                      onClick={() => setUserGender('female')}
                      style={{
                        ...styles.canvasAvatarCard,
                        border: userGender === 'female' ? '4px solid #4CAF50' : '4px solid rgba(255,255,255,0.5)',
                      }}
                    >
                      <div style={{ fontSize: '80px' }}>👩</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '10px' }}>Female</div>
                      {userGender === 'female' && <div style={styles.checkmark}>✓</div>}
                    </button>
                  </div>

                  <button
                    onClick={startConversationAfterAvatarSelection}
                    style={styles.startConversationBtn}
                  >
                    Start Conversation →
                  </button>

                  <button
                    onClick={() => {
                      setShowPreview(false);
                      setShowAvatarSelection(false);
                    }}
                    style={styles.closeOverlayBtn}
                  >
                    ✕ Close
                  </button>
                </div>
              )}

              {/* Conversation Screen */}
              {!showAvatarSelection && previewCurrentNodeId && (
                <>
                  {previewCurrentNodeId === 'END' ? (
                    <div style={styles.overlayContent}>
                      <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
                      <h2 style={{ fontSize: '24px', marginBottom: '10px', color: 'white' }}>Conversation Ended</h2>
                      <button
                        onClick={() => {
                          setShowPreview(false);
                          setPreviewCurrentNodeId(null);
                        }}
                        style={styles.startConversationBtn}
                      >
                        ← Back to Editor
                      </button>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const currentNode = getCharacterById(activeConversationCharId).conversations.find(n => n.id === previewCurrentNodeId);
                        if (!currentNode) return null;

                        return (
                          <div style={styles.gameDialogueOverlay}>
                            {/* Character and Dialogue */}
                            <div style={styles.dialogueTop}>
                              <div style={styles.canvasCharacterPortrait}>
                                <div style={{ fontSize: '80px' }}>
                                  {getCharacterById(activeConversationCharId).emoji}
                                </div>
                              </div>
                              <div style={styles.canvasDialogueBubble}>
                                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                                  {getCharacterById(activeConversationCharId).name}
                                </div>
                                {currentNode.text}
                              </div>
                            </div>

                            {/* Player Responses */}
                            {currentNode.options && currentNode.options.length > 0 && (
                              <div style={styles.canvasResponsesBottom}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                  <div style={{ fontSize: '40px' }}>{userAvatar}</div>
                                  <div style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                                    Choose your response:
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  {currentNode.options.map((opt, i) => (
                                    <button
                                      key={opt.id}
                                      onClick={() => {
                                        if (opt.nextNodeId) {
                                          setPreviewCurrentNodeId(opt.nextNodeId);
                                        } else {
                                          setPreviewCurrentNodeId('END');
                                        }
                                      }}
                                      style={styles.canvasResponseBtn}
                                    >
                                      <span style={{ fontSize: '18px', marginRight: '10px', opacity: 0.7 }}>
                                        {String.fromCharCode(65 + i)}
                                      </span>
                                      {opt.text}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {(!currentNode.options || currentNode.options.length === 0) && (
                              <div style={styles.canvasResponsesBottom}>
                                <button
                                  onClick={() => {
                                    setShowPreview(false);
                                    setPreviewCurrentNodeId(null);
                                  }}
                                  style={styles.startConversationBtn}
                                >
                                  ← Back to Editor
                                </button>
                              </div>
                            )}

                            <button
                              onClick={() => {
                                setShowPreview(false);
                                setPreviewCurrentNodeId(null);
                                setShowAvatarSelection(false);
                              }}
                              style={styles.closeOverlayBtn}
                            >
                              ✕ Close Preview
                            </button>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Control Panel */}
        <div style={styles.controlPanel}>
          {/* Main Tabs */}
          <div style={styles.mainTabs}>
            <button 
              onClick={() => setRightPanelTab('elements')}
              style={{
                ...styles.mainTabBtn,
                ...(rightPanelTab === 'elements' ? styles.mainTabBtnActive : {})
              }}
            >
              🎨 Elements
            </button>
            <button 
              onClick={() => setRightPanelTab('conversations')}
              style={{
                ...styles.mainTabBtn,
                ...(rightPanelTab === 'conversations' ? styles.mainTabBtnActive : {})
              }}
            >
              💬 Conversations
            </button>
            <button 
              onClick={() => setRightPanelTab('settings')}
              style={{
                ...styles.mainTabBtn,
                ...(rightPanelTab === 'settings' ? styles.mainTabBtnActive : {})
              }}
            >
              ⚙️ Settings
            </button>
          </div>

          <hr style={styles.divider} />

          {/* ELEMENTS TAB */}
          {rightPanelTab === 'elements' && (
            <>
              {/* Quick Save Button */}
              <button 
                onClick={saveGameConfig} 
                disabled={isSaving}
                style={{
                  ...styles.quickSaveBtn,
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                {isSaving ? '⏳ Saving...' : '💾 Save All Changes'}
              </button>
              {saveMessage && (
                <div style={{
                  marginBottom: '15px',
                  padding: '10px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  background: saveMessage.includes('✅') ? '#E8F5E9' : '#FFEBEE',
                  color: saveMessage.includes('✅') ? '#2E7D32' : '#C62828',
                  fontWeight: 'bold',
                  fontSize: '13px'
                }}>
                  {saveMessage}
                </div>
              )}

              <hr style={styles.divider} />

              <div style={styles.elementSelector}>
                <button 
                  onClick={() => setActiveElement('taxi')}
                  style={{
                    ...styles.selectorBtn,
                    ...(activeElement === 'taxi' ? styles.selectorBtnActive : {})
                  }}
                >
                  🚕 Taxi
                </button>
                <button 
                  onClick={() => setActiveElement('character')}
                  style={{
                    ...styles.selectorBtn,
                    ...(activeElement === 'character' ? styles.selectorBtnActive : {})
                  }}
                >
                  🧕 Character
                </button>
                <button 
                  onClick={() => setActiveElement('gems')}
                  style={{
                    ...styles.selectorBtn,
                    ...(activeElement === 'gems' ? styles.selectorBtnActive : {})
                  }}
                >
                  💎 Place Gems
                </button>
              </div>

          {activeElement === 'taxi' && (
            <>
              <h2 style={styles.sectionTitle}>🚕 Taxi Position</h2>
              <div style={styles.controlGroup}>
                <label style={styles.label}>X-Axis (Left ← → Right): {Math.round(taxiPos.x)}</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1200" 
                  value={taxiPos.x}
                  onChange={(e) => setTaxiPos({...taxiPos, x: parseInt(e.target.value)})}
                  style={styles.slider}
                />
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Y-Axis (Top ↑ ↓ Down): {Math.round(taxiPos.y)}</label>
                <input 
                  type="range" 
                  min="0" 
                  max="800" 
                  value={taxiPos.y}
                  onChange={(e) => setTaxiPos({...taxiPos, y: parseInt(e.target.value)})}
                  style={styles.slider}
                />
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Z-Axis (Back ↗ ↙ Front): {taxiPos.z}</label>
                <input 
                  type="range" 
                  min="-100" 
                  max="200" 
                  value={taxiPos.z}
                  onChange={(e) => setTaxiPos({...taxiPos, z: parseInt(e.target.value)})}
                  style={styles.slider}
                />
                <div style={styles.axisHint}>💡 Isometric depth/height</div>
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Size: {taxiSize}px</label>
                <input 
                  type="range" 
                  min="20" 
                  max="150" 
                  value={taxiSize}
                  onChange={(e) => setTaxiSize(parseInt(e.target.value))}
                  style={styles.slider}
                />
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Manual Rotation: {taxiRotation}°</label>
                <input 
                  type="range" 
                  min="0" 
                  max="360" 
                  value={taxiRotation}
                  onChange={(e) => setTaxiRotation(parseInt(e.target.value))}
                  style={styles.slider}
                />
                <div style={styles.axisHint}>💡 0°=Right, 90°=Down, 180°=Left, 270°=Up</div>
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Auto-Rotation Offset: {taxiRotationOffset}°</label>
                <input 
                  type="range" 
                  min="-180" 
                  max="180" 
                  value={taxiRotationOffset}
                  onChange={(e) => setTaxiRotationOffset(parseInt(e.target.value))}
                  style={styles.slider}
                />
                <div style={styles.axisHint}>💡 Adjust if taxi faces wrong way during animation</div>
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Flip & Mirror:</label>
                <div style={styles.flipControls}>
                  <button 
                    onClick={() => setTaxiFlipH(!taxiFlipH)}
                    style={{
                      ...styles.flipBtn,
                      ...(taxiFlipH ? styles.flipBtnActive : {})
                    }}
                  >
                    ↔️ Flip Horizontal
                  </button>
                  <button 
                    onClick={() => setTaxiFlipV(!taxiFlipV)}
                    style={{
                      ...styles.flipBtn,
                      ...(taxiFlipV ? styles.flipBtnActive : {})
                    }}
                  >
                    ↕️ Flip Vertical
                  </button>
                </div>
              </div>
              
              <div style={styles.controlGroup}>
                <label style={styles.label}>Taxi Style:</label>
                <div style={styles.styleSelector}>
                  <button 
                    onClick={() => setTaxiStyle('classic')}
                    style={{
                      ...styles.styleBtn,
                      ...(taxiStyle === 'classic' ? styles.styleBtnActive : {})
                    }}
                  >
                    🚕 Classic
                  </button>
                  <button 
                    onClick={() => setTaxiStyle('modern')}
                    style={{
                      ...styles.styleBtn,
                      ...(taxiStyle === 'modern' ? styles.styleBtnActive : {})
                    }}
                  >
                    🚗 Modern
                  </button>
                  <button 
                    onClick={() => setTaxiStyle('van')}
                    style={{
                      ...styles.styleBtn,
                      ...(taxiStyle === 'van' ? styles.styleBtnActive : {})
                    }}
                  >
                    🚐 Van
                  </button>
                </div>
              </div>
            </>
          )}

          {activeElement === 'character' && (
            <>
              <h2 style={styles.sectionTitle}>👥 Characters Manager ({characters.length})</h2>
              
              {/* Add New Character */}
              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>➕ Add New Character</label>
                <input 
                  type="text"
                  value={newCharacterName}
                  onChange={(e) => setNewCharacterName(e.target.value)}
                  placeholder="Character name (e.g., 'Chef', 'Guide')"
                  style={styles.textInput}
                />
                <button onClick={addNewCharacter} style={styles.addMarkerBtn}>
                  ➕ Add Character
                </button>
              </div>

              <hr style={styles.divider} />

              {/* Character List */}
              <div style={styles.markersList}>
                {characters.map((character) => (
                  <div 
                    key={character.id} 
                    style={{
                      ...styles.markerItem,
                      background: character.id === selectedCharacterId ? '#E8F5E9' : '#F5F5F5',
                      border: character.id === selectedCharacterId ? '2px solid #4CAF50' : 'none'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      {character.customImage ? '🖼️' : character.emoji}
                    </div>
                    <div style={styles.markerInfo}>
                      <div style={styles.markerNameText}>{character.name}</div>
                      <div style={styles.markerCoords}>
                        x:{Math.round(character.position.x)} y:{Math.round(character.position.y)} z:{character.position.z}
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCharacterId(character.id)}
                      style={{
                        ...styles.markerGoBtn,
                        background: character.id === selectedCharacterId ? '#4CAF50' : '#2196F3'
                      }}
                    >
                      {character.id === selectedCharacterId ? '✓' : '✏️'}
                    </button>
                    {characters.length > 1 && (
                      <button 
                        onClick={() => deleteCharacter(character.id)}
                        style={styles.markerDeleteBtn}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <hr style={styles.divider} />

              {/* Selected Character Controls */}
              {getSelectedCharacter() && (
                <>
                  <h2 style={styles.sectionTitle}>
                    ✏️ Edit: {getSelectedCharacter().name}
                  </h2>

                  {/* Upload Custom Image */}
                  <div style={styles.controlGroup}>
                    <label style={styles.uploadLabel}>🖼️ Character Image</label>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={handleCharacterImageUpload}
                      style={styles.fileInput}
                    />
                    {getSelectedCharacter().customImage && (
                      <div style={styles.uploadedInfo}>
                        ✅ Custom image uploaded!
                      </div>
                    )}
                  </div>

                  {/* Position Controls */}
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      X-Axis (Left ← → Right): {Math.round(getSelectedCharacter().position.x)}
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="1200" 
                      value={getSelectedCharacter().position.x}
                      onChange={(e) => updateSelectedCharacter({
                        position: { ...getSelectedCharacter().position, x: parseInt(e.target.value) }
                      })}
                      style={styles.slider}
                    />
                  </div>
                  
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      Y-Axis (Top ↑ ↓ Down): {Math.round(getSelectedCharacter().position.y)}
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="800" 
                      value={getSelectedCharacter().position.y}
                      onChange={(e) => updateSelectedCharacter({
                        position: { ...getSelectedCharacter().position, y: parseInt(e.target.value) }
                      })}
                      style={styles.slider}
                    />
                  </div>
                  
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      Z-Axis (Back ↗ ↙ Front): {getSelectedCharacter().position.z}
                    </label>
                    <input 
                      type="range" 
                      min="-100" 
                      max="200" 
                      value={getSelectedCharacter().position.z}
                      onChange={(e) => updateSelectedCharacter({
                        position: { ...getSelectedCharacter().position, z: parseInt(e.target.value) }
                      })}
                      style={styles.slider}
                    />
                    <div style={styles.axisHint}>💡 Isometric depth/height</div>
                  </div>
                  
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>
                      Size: {getSelectedCharacter().size}px
                    </label>
                    <input 
                      type="range" 
                      min="40" 
                      max="200" 
                      value={getSelectedCharacter().size}
                      onChange={(e) => updateSelectedCharacter({
                        size: parseInt(e.target.value)
                      })}
                      style={styles.slider}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* GEM PLACEMENT */}
          {activeElement === 'gems' && (
            <>
              <h2 style={styles.sectionTitle}>💎 Gem Placement</h2>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', padding: '0 15px' }}>
                Click on the canvas to place hidden gems. Players will find these to earn currency!
              </p>

              {/* Gem Type Selector */}
              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>Select Gem Type to Place:</label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    onClick={() => setPlacingGem('ruby')}
                    style={{
                      ...styles.gemTypeBtn,
                      border: placingGem === 'ruby' ? '3px solid #F44336' : '2px solid #E0E0E0',
                      background: placingGem === 'ruby' ? '#FFEBEE' : 'white'
                    }}
                  >
                    ❤️<br/>Ruby<br/><small>100 gems</small>
                  </button>
                  <button
                    onClick={() => setPlacingGem('emerald')}
                    style={{
                      ...styles.gemTypeBtn,
                      border: placingGem === 'emerald' ? '3px solid #4CAF50' : '2px solid #E0E0E0',
                      background: placingGem === 'emerald' ? '#E8F5E9' : 'white'
                    }}
                  >
                    💚<br/>Emerald<br/><small>150 gems</small>
                  </button>
                  <button
                    onClick={() => setPlacingGem('diamond')}
                    style={{
                      ...styles.gemTypeBtn,
                      border: placingGem === 'diamond' ? '3px solid #2196F3' : '2px solid #E0E0E0',
                      background: placingGem === 'diamond' ? '#E3F2FD' : 'white'
                    }}
                  >
                    💎<br/>Diamond<br/><small>300 gems</small>
                  </button>
                </div>
                {placingGem && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    background: '#FFF3E0',
                    borderRadius: '8px',
                    fontSize: '13px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}>
                    ✨ Click on canvas to place {placingGem}!
                  </div>
                )}
              </div>

              {/* Placed Gems List */}
              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>Placed Gems ({hiddenGems.length}):</label>
                {hiddenGems.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#999', textAlign: 'center', padding: '20px' }}>
                    No gems placed yet. Select a gem type and click on the canvas!
                  </p>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {hiddenGems.map((gem, idx) => (
                      <div key={gem.id} style={styles.gemListItem}>
                        <span style={{ fontSize: '20px' }}>
                          {gem.type === 'diamond' ? '💎' : gem.type === 'emerald' ? '💚' : '❤️'}
                        </span>
                        <div style={{ flex: 1, fontSize: '12px' }}>
                          <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{gem.type}</div>
                          <div style={{ color: '#666' }}>x:{gem.x} y:{gem.y} ({gem.value} gems)</div>
                        </div>
                        <button
                          onClick={() => {
                            setHiddenGems(hiddenGems.filter(g => g.id !== gem.id));
                          }}
                          style={{
                            background: '#F44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {hiddenGems.length > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm('Clear all placed gems?')) {
                        setHiddenGems([]);
                      }
                    }}
                    style={{
                      width: '100%',
                      marginTop: '10px',
                      padding: '10px',
                      background: '#F44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    🗑️ Clear All Gems
                  </button>
                )}
              </div>
            </>
          )}

          <hr style={styles.divider} />

          <h2 style={styles.sectionTitle}>⚙️ Animation Settings</h2>
          
          <div style={styles.controlGroup}>
            <button 
              onClick={() => setUseAutoRotation(!useAutoRotation)}
              style={{
                ...styles.autoRotateToggle,
                ...(useAutoRotation ? styles.autoRotateToggleOn : styles.autoRotateToggleOff)
              }}
            >
              {useAutoRotation ? '🔄 Auto-Rotation: ON' : '✋ Manual Rotation: ON'}
            </button>
            <div style={styles.rotationHint}>
              {useAutoRotation 
                ? '💡 Taxi will auto-rotate based on movement direction' 
                : '💡 Taxi will use rotation you set for each marker'}
            </div>
          </div>
          
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              Animation Speed: {animationSpeed.toFixed(4)} 
              <span style={{marginLeft: '10px', fontSize: '12px', color: '#666'}}>
                ({animationSpeed <= 0.002 ? '🐌 Very Slow' : 
                  animationSpeed <= 0.005 ? '🐢 Slow' : 
                  animationSpeed <= 0.01 ? '🚶 Normal' : 
                  animationSpeed <= 0.015 ? '🏃 Fast' : '🚀 Very Fast'})
              </span>
            </label>
            <input 
              type="range" 
              min="0.001" 
              max="0.03" 
              step="0.001"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              style={styles.slider}
            />
            <div style={styles.speedLabels}>
              <span style={styles.speedLabel}>0.001 (Slowest)</span>
              <span style={styles.speedLabel}>0.03 (Fastest)</span>
            </div>
          </div>

          <hr style={styles.divider} />

          <h2 style={styles.sectionTitle}>📍 Add Marker</h2>
          <div style={styles.controlGroup}>
            <input 
              type="text"
              value={markerName}
              onChange={(e) => setMarkerName(e.target.value)}
              placeholder="Marker name (e.g., 'Hotel entrance')"
              style={styles.textInput}
            />
            <textarea 
              value={markerNote}
              onChange={(e) => setMarkerNote(e.target.value)}
              placeholder="Add note/dialogue (optional) - Shows when taxi stops here"
              style={styles.textArea}
              rows="3"
            />
            <button onClick={addMarker} style={styles.addMarkerBtn}>
              ➕ Add Marker at Current Position
            </button>
          </div>

          <hr style={styles.divider} />

          <h2 style={styles.sectionTitle}>🗺️ Saved Markers ({markers.length})</h2>
          <div style={styles.markersList}>
            {markers.length === 0 && (
              <div style={styles.emptyMarkers}>No markers yet. Add one!</div>
            )}
            {markers.map((marker, index) => (
              <div key={marker.id} style={styles.markerItem}>
                <div style={styles.markerNumber}>{index + 1}</div>
                <div style={styles.markerInfo}>
                  <div style={styles.markerNameText}>
                    {marker.name} {marker.note && '💬'}
                  </div>
                  <div style={styles.markerCoords}>
                    X:{marker.position.x}, Y:{marker.position.y}, Z:{marker.position.z}
                    {marker.rotation !== undefined && ` | Rot:${Math.round(marker.rotation)}°`}
                  </div>
                  {marker.note && (
                    <div style={styles.markerNotePreview}>
                      📝 {marker.note.substring(0, 30)}{marker.note.length > 30 ? '...' : ''}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => goToMarker(marker)} 
                  style={styles.markerGoBtn}
                  title="Go to this position"
                >
                  📍
                </button>
                <button 
                  onClick={() => deleteMarker(marker.id)} 
                  style={styles.markerDeleteBtn}
                  title="Delete marker"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
          
          {markers.length > 0 && (
            <>
              <div style={styles.playControls}>
                {!isPlaying ? (
                  <button onClick={playAnimation} style={styles.playBtn}>
                    ▶️ PLAY Animation
                  </button>
                ) : (
                  <button onClick={stopAnimation} style={styles.stopBtn}>
                    ⏸️ STOP
                  </button>
                )}
                <button onClick={resetToStart} style={styles.resetBtn}>
                  🔄 Reset to Start
                </button>
              </div>
              
              {isPlaying && (
                <div style={styles.animationStatus}>
                  ✅ Playing: Marker {currentMarkerIndex + 1} of {markers.length}
                </div>
              )}
              
              <div style={styles.markerActions}>
                <button onClick={copyAllMarkers} style={styles.actionBtn}>
                  📋 Copy All
                </button>
                <button onClick={exportPath} style={styles.actionBtn}>
                  📤 Export Path
                </button>
                <button onClick={() => setMarkers([])} style={styles.clearBtn}>
                  🗑️ Clear All
                </button>
              </div>
            </>
          )}

          <hr style={styles.divider} />

          <h2 style={styles.sectionTitle}>🖼️ Background Controls</h2>
          <div style={styles.controlGroup}>
            <label style={styles.label}>Zoom: {bgZoom.toFixed(2)}x</label>
            <input 
              type="range" 
              min="0.5" 
              max="3" 
              step="0.1"
              value={bgZoom}
              onChange={(e) => setBgZoom(parseFloat(e.target.value))}
              style={styles.slider}
            />
          </div>

          <hr style={styles.divider} />

          <h2 style={styles.sectionTitle}>📤 Upload Custom Images</h2>
          
          <div style={styles.controlGroup}>
            <label style={styles.uploadLabel}>
              🖼️ Background Image:
            </label>
            <input 
              type="file" 
              accept="image/*"
              onChange={handleBackgroundUpload}
              style={styles.fileInput}
            />
            {customBgImage && (
              <div style={styles.uploadedInfo}>✅ Custom background loaded!</div>
            )}
          </div>

          <div style={styles.controlGroup}>
            <label style={styles.uploadLabel}>
              🚕 Taxi Image:
            </label>
            <input 
              type="file" 
              accept="image/*"
              onChange={handleTaxiImageUpload}
              style={styles.fileInput}
            />
            {customTaxiImage && (
              <div style={styles.uploadedInfo}>✅ Custom taxi loaded!</div>
            )}
          </div>

          <button onClick={resetToDefaults} style={styles.resetImagesBtn}>
            🔄 Reset to Default Images
          </button>

          <hr style={styles.divider} />

          <div style={styles.coordinatesBox}>
            <h3 style={styles.coordsTitle}>📍 Current Position:</h3>
            {activeElement === 'taxi' && (
              <div style={styles.coordsText}>
                <strong>🚕 Taxi:</strong><br/>
                X: {Math.round(taxiPos.x)}, Y: {Math.round(taxiPos.y)}, Z: {taxiPos.z}<br/>
                Size: {taxiSize}px
              </div>
            )}
            {activeElement === 'character' && getSelectedCharacter() && (
              <div style={styles.coordsText}>
                <strong>👤 {getSelectedCharacter().name}:</strong><br/>
                X: {Math.round(getSelectedCharacter().position.x)}, Y: {Math.round(getSelectedCharacter().position.y)}, Z: {getSelectedCharacter().position.z}<br/>
                Size: {getSelectedCharacter().size}px
              </div>
            )}
          </div>

          <div style={styles.instructionsBox}>
            <h3 style={styles.instructionsTitle}>💡 How to Use:</h3>
            <ul style={styles.instructionsList}>
              <li>Switch between Taxi/Character</li>
              <li>Drag element or use X/Y/Z sliders</li>
              <li><strong>X-Axis:</strong> Left ← → Right</li>
              <li><strong>Y-Axis:</strong> Top ↑ ↓ Down</li>
              <li><strong>Z-Axis:</strong> Front/Back depth</li>
              <li>Click &quot;Add Marker&quot; to save position</li>
              <li>Create path with multiple markers</li>
              <li>Export path as JSON</li>
            </ul>
          </div>
            </>
          )}

          {/* CONVERSATIONS TAB */}
          {rightPanelTab === 'conversations' && (
            <>
              <h2 style={styles.sectionTitle}>💬 Conversations Builder</h2>
              
              {/* User Avatar Selection */}
              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>👤 Your Avatar (Player)</label>
                <div style={styles.genderSelector}>
                  <button
                    onClick={() => setUserGender('male')}
                    style={{
                      ...styles.genderBtn,
                      ...(userGender === 'male' ? styles.genderBtnActive : {})
                    }}
                  >
                    👨 Male
                  </button>
                  <button
                    onClick={() => setUserGender('female')}
                    style={{
                      ...styles.genderBtn,
                      ...(userGender === 'female' ? styles.genderBtnActive : {})
                    }}
                  >
                    👩 Female
                  </button>
                </div>
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '48px', 
                  margin: '10px 0',
                  padding: '10px',
                  background: '#F5F5F5',
                  borderRadius: '10px'
                }}>
                  {userAvatar}
                </div>
              </div>

              <hr style={styles.divider} />

              {/* Character Tabs for Conversations */}
              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>🎭 Select Character</label>
                <div style={styles.characterTabs}>
                  {characters.map(char => (
                    <button
                      key={char.id}
                      onClick={() => setActiveConversationCharId(char.id)}
                      style={{
                        ...styles.characterTab,
                        ...(activeConversationCharId === char.id ? styles.characterTabActive : {})
                      }}
                    >
                      {char.customImage ? '🖼️' : char.emoji}
                      <br />
                      <span style={{ fontSize: '11px' }}>{char.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <hr style={styles.divider} />

              {/* Conversation Flow for Selected Character */}
              {activeConversationCharId && getCharacterById(activeConversationCharId) && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={styles.sectionTitle}>
                      💭 {getCharacterById(activeConversationCharId).name}'s Dialogue
                    </h3>
                    <button
                      onClick={() => startPreview(activeConversationCharId)}
                      style={{
                        padding: '10px 15px',
                        background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '13px'
                      }}
                    >
                      👁️ Preview
                    </button>
                  </div>

                  <div style={styles.conversationBuilder}>
                    {getCharacterById(activeConversationCharId).conversations.length === 0 ? (
                      <div style={styles.emptyState}>
                        <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                          No dialogue yet. Add the first message!
                        </p>
                      </div>
                    ) : (
                      <div style={styles.conversationList}>
                        {getCharacterById(activeConversationCharId).conversations.map((node, index) => (
                          <div key={node.id} style={{
                            ...styles.conversationNode,
                            border: node.isStart ? '3px solid #4CAF50' : '2px solid #E0E0E0'
                          }}>
                            <div style={styles.nodeHeader}>
                              <div>
                                <strong>Node {index + 1}</strong>
                                {node.isStart && <span style={{ 
                                  marginLeft: '8px', 
                                  background: '#4CAF50', 
                                  color: 'white', 
                                  padding: '2px 8px', 
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}>START</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                {!node.isStart && (
                                  <button
                                    onClick={() => setStartNode(activeConversationCharId, node.id)}
                                    style={{ 
                                      ...styles.nodeActionBtn,
                                      background: '#4CAF50'
                                    }}
                                    title="Set as start node"
                                  >
                                    ⭐
                                  </button>
                                )}
                                <button
                                  onClick={() => setEditingNodeId(editingNodeId === node.id ? null : node.id)}
                                  style={{ 
                                    ...styles.nodeActionBtn,
                                    background: editingNodeId === node.id ? '#FF9800' : '#2196F3'
                                  }}
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Delete this dialogue node?')) {
                                      deleteConversationNode(activeConversationCharId, node.id);
                                    }
                                  }}
                                  style={styles.deleteNodeBtn}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>

                            <div style={styles.nodeContent}>
                              {/* Character's Message */}
                              <div style={{ marginBottom: '10px' }}>
                                <strong style={{ fontSize: '12px', color: '#666' }}>
                                  {getCharacterById(activeConversationCharId).emoji} Character says:
                                </strong>
                                <p style={{ 
                                  background: '#E3F2FD', 
                                  padding: '10px', 
                                  borderRadius: '8px',
                                  margin: '5px 0',
                                  fontSize: '13px'
                                }}>
                                  {node.text}
                                </p>
                              </div>

                              {/* Player Response Options */}
                              {node.options && node.options.length > 0 && (
                                <div style={{ marginTop: '10px' }}>
                                  <strong style={{ fontSize: '12px', color: '#666' }}>
                                    {userAvatar} Player choices:
                                  </strong>
                                  {node.options.map((opt, i) => {
                                    const nextNode = getCharacterById(activeConversationCharId).conversations.find(n => n.id === opt.nextNodeId);
                                    return (
                                      <div key={opt.id} style={{ 
                                        background: '#FFF3E0', 
                                        padding: '8px', 
                                        borderRadius: '6px',
                                        margin: '5px 0',
                                        fontSize: '12px',
                                        border: '2px solid #FFB74D'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span><strong>{i + 1}.</strong> {opt.text}</span>
                                          <button
                                            onClick={() => deleteResponseOption(activeConversationCharId, node.id, opt.id)}
                                            style={{
                                              background: '#F44336',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '4px 8px',
                                              cursor: 'pointer',
                                              fontSize: '10px'
                                            }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                          → Goes to: {nextNode ? `Node ${getCharacterById(activeConversationCharId).conversations.indexOf(nextNode) + 1}` : '(End conversation)'}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Edit Mode: Add Player Responses */}
                              {editingNodeId === node.id && (
                                <div style={{ 
                                  marginTop: '10px', 
                                  padding: '10px', 
                                  background: '#FFF9E6',
                                  borderRadius: '8px',
                                  border: '2px dashed #FFB74D'
                                }}>
                                  <h4 style={{ fontSize: '12px', marginBottom: '8px' }}>
                                    ➕ Add Player Response
                                  </h4>
                                  <input
                                    type="text"
                                    placeholder="What can player say? (e.g., 'Yes, teach me!')"
                                    style={styles.textInput}
                                    id={`optText_${node.id}`}
                                  />
                                  <select
                                    style={{
                                      ...styles.textInput,
                                      marginBottom: '10px'
                                    }}
                                    id={`optNext_${node.id}`}
                                  >
                                    <option value="">→ End conversation</option>
                                    {getCharacterById(activeConversationCharId).conversations
                                      .filter(n => n.id !== node.id)
                                      .map((n, idx) => (
                                        <option key={n.id} value={n.id}>
                                          → Go to Node {getCharacterById(activeConversationCharId).conversations.indexOf(n) + 1}
                                        </option>
                                      ))
                                    }
                                  </select>
                                  <button
                                    onClick={() => {
                                      const text = document.getElementById(`optText_${node.id}`).value;
                                      const nextId = document.getElementById(`optNext_${node.id}`).value || null;
                                      if (text.trim()) {
                                        addResponseOption(activeConversationCharId, node.id, text.trim(), nextId);
                                        document.getElementById(`optText_${node.id}`).value = '';
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      background: '#FFB74D',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    ➕ Add Response Option
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Dialogue Node */}
                    <div style={styles.addNodeSection}>
                      <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>➕ Add New Dialogue Node</h4>
                      <textarea
                        placeholder="What does the character say? (e.g., 'Marhaba! Welcome to Morocco!')"
                        style={styles.textArea}
                        rows="3"
                        id={`charText_${activeConversationCharId}`}
                      />
                      <button
                        onClick={() => {
                          const text = document.getElementById(`charText_${activeConversationCharId}`).value;
                          if (text.trim()) {
                            const isFirstNode = getCharacterById(activeConversationCharId).conversations.length === 0;
                            addConversationNode(activeConversationCharId, text.trim(), isFirstNode);
                            document.getElementById(`charText_${activeConversationCharId}`).value = '';
                          }
                        }}
                        style={styles.addNodeBtn}
                      >
                        ➕ Add Dialogue Node
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Preview is now shown on canvas overlay */}
              {false && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={styles.sectionTitle}>
                      👁️ Preview Mode
                    </h3>
                    <button
                      onClick={() => {
                        setShowPreview(false);
                        setPreviewCurrentNodeId(null);
                        setShowAvatarSelection(false);
                      }}
                      style={{
                        padding: '10px 15px',
                        background: '#9E9E9E',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '13px'
                      }}
                    >
                      ✕ Close Preview
                    </button>
                  </div>

                  {/* Avatar Selection Screen */}
                  {showAvatarSelection && (
                    <div style={styles.previewContainer}>
                      <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>
                          🎭
                        </div>
                        <h2 style={{ fontSize: '28px', marginBottom: '15px', fontWeight: 'bold' }}>
                          Choose Your Character
                        </h2>
                        <p style={{ fontSize: '16px', marginBottom: '30px', opacity: 0.9 }}>
                          Select your avatar to continue
                        </p>

                        {/* Avatar Selection Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '20px',
                          justifyContent: 'center',
                          marginBottom: '30px'
                        }}>
                          <button
                            onClick={() => setUserGender('male')}
                            style={{
                              ...styles.avatarSelectionCard,
                              border: userGender === 'male' ? '4px solid #4CAF50' : '4px solid rgba(255,255,255,0.3)',
                              transform: userGender === 'male' ? 'scale(1.05)' : 'scale(1)'
                            }}
                          >
                            <div style={{ fontSize: '80px', marginBottom: '10px' }}>👨</div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Male</div>
                            {userGender === 'male' && (
                              <div style={{ 
                                position: 'absolute', 
                                top: '10px', 
                                right: '10px',
                                background: '#4CAF50',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px'
                              }}>
                                ✓
                              </div>
                            )}
                          </button>

                          <button
                            onClick={() => setUserGender('female')}
                            style={{
                              ...styles.avatarSelectionCard,
                              border: userGender === 'female' ? '4px solid #4CAF50' : '4px solid rgba(255,255,255,0.3)',
                              transform: userGender === 'female' ? 'scale(1.05)' : 'scale(1)'
                            }}
                          >
                            <div style={{ fontSize: '80px', marginBottom: '10px' }}>👩</div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Female</div>
                            {userGender === 'female' && (
                              <div style={{ 
                                position: 'absolute', 
                                top: '10px', 
                                right: '10px',
                                background: '#4CAF50',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px'
                              }}>
                                ✓
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Continue Button */}
                        <button
                          onClick={startConversationAfterAvatarSelection}
                          style={{
                            padding: '15px 40px',
                            background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            transition: 'all 0.2s'
                          }}
                        >
                          Start Conversation →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Conversation Screen */}
                  {!showAvatarSelection && previewCurrentNodeId && (
                    <div style={styles.previewContainer}>
                    {(() => {
                      // Handle conversation end
                      if (previewCurrentNodeId === 'END') {
                        return (
                          <div style={{ textAlign: 'center', color: 'white' }}>
                            <div style={{ fontSize: '64px', marginBottom: '20px' }}>
                              ✅
                            </div>
                            <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>Conversation Ended</h2>
                            <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                              This is where the player's conversation ends
                            </p>
                            <button
                              onClick={() => {
                                setShowPreview(false);
                                setPreviewCurrentNodeId(null);
                              }}
                              style={{
                                padding: '12px 24px',
                                background: 'white',
                                color: '#667eea',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}
                            >
                              ← Back to Editor
                            </button>
                          </div>
                        );
                      }
                      
                      const currentNode = getCharacterById(activeConversationCharId).conversations.find(n => n.id === previewCurrentNodeId);
                      if (!currentNode) return <p style={{ color: 'white' }}>Node not found</p>;
                      
                      return (
                        <>
                          {/* Character Message - Side by Side Layout */}
                          <div style={styles.gameDialogueContainer}>
                            {/* Character Portrait on Left */}
                            <div style={styles.characterPortrait}>
                              <div style={{ 
                                fontSize: '80px',
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                width: '120px',
                                height: '120px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '4px solid white',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                              }}>
                                {getCharacterById(activeConversationCharId).customImage ? '🖼️' : getCharacterById(activeConversationCharId).emoji}
                              </div>
                              <div style={{ 
                                marginTop: '10px', 
                                fontSize: '14px', 
                                fontWeight: 'bold',
                                color: 'white',
                                textAlign: 'center'
                              }}>
                                {getCharacterById(activeConversationCharId).name}
                              </div>
                            </div>

                            {/* Dialogue Bubble on Right */}
                            <div style={styles.dialogueBubbleContainer}>
                              <div style={styles.dialogueBubble}>
                                {currentNode.text}
                              </div>
                            </div>
                          </div>

                          {/* Player Response Options at Bottom */}
                          {currentNode.options && currentNode.options.length > 0 && (
                            <div style={styles.responseOptionsContainer}>
                              {/* Player Avatar */}
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                marginBottom: '15px',
                                gap: '10px'
                              }}>
                                <div style={{ 
                                  fontSize: '40px',
                                  background: 'rgba(255,255,255,0.9)',
                                  borderRadius: '50%',
                                  width: '60px',
                                  height: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '3px solid #FFB74D'
                                }}>
                                  {userAvatar}
                                </div>
                                <div style={{ 
                                  color: 'white', 
                                  fontSize: '14px', 
                                  fontWeight: 'bold' 
                                }}>
                                  Choose your response:
                                </div>
                              </div>

                              {/* Response Buttons */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {currentNode.options.map((opt, i) => (
                                  <button
                                    key={opt.id}
                                    onClick={() => {
                                      if (opt.nextNodeId) {
                                        setPreviewCurrentNodeId(opt.nextNodeId);
                                      } else {
                                        setPreviewCurrentNodeId('END');
                                      }
                                    }}
                                    style={styles.gameResponseBtn}
                                  >
                                    <span style={{ 
                                      fontSize: '18px', 
                                      marginRight: '10px',
                                      opacity: 0.7
                                    }}>
                                      {String.fromCharCode(65 + i)}
                                    </span>
                                    {opt.text}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {(!currentNode.options || currentNode.options.length === 0) && (
                            <div style={styles.responseOptionsContainer}>
                              <div style={{ textAlign: 'center' }}>
                                <p style={{ color: 'white', fontSize: '13px', marginBottom: '15px', opacity: 0.8 }}>
                                  (No response options - Conversation ends)
                                </p>
                                <button
                                  onClick={() => {
                                    setShowPreview(false);
                                    setPreviewCurrentNodeId(null);
                                  }}
                                  style={{
                                    padding: '12px 24px',
                                    background: 'white',
                                    color: '#667eea',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                  }}
                                >
                                  ← Back to Editor
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* SETTINGS TAB */}
          {rightPanelTab === 'settings' && (
            <>
              <h2 style={styles.sectionTitle}>⚙️ Game Settings</h2>
              
              {/* Save Configuration Button */}
              <div style={styles.controlGroup}>
                <button 
                  onClick={saveGameConfig} 
                  disabled={isSaving}
                  style={{
                    ...styles.saveConfigBtn,
                    opacity: isSaving ? 0.6 : 1,
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSaving ? '⏳ Saving...' : '💾 Save All Configuration'}
                </button>
                {saveMessage && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    background: saveMessage.includes('✅') ? '#E8F5E9' : '#FFEBEE',
                    color: saveMessage.includes('✅') ? '#2E7D32' : '#C62828',
                    fontWeight: 'bold'
                  }}>
                    {saveMessage}
                  </div>
                )}
                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                  💡 This will save background, characters, conversations, and all settings
                </p>
              </div>

              <hr style={styles.divider} />

              {/* Currency Settings */}
              <h3 style={styles.sectionTitle}>💰 Currency Settings</h3>
              <div style={styles.controlGroup}>
                <label style={styles.label}>Starting Gems (First Visit):</label>
                <input 
                  type="number" 
                  min="0" 
                  max="10000"
                  step="100"
                  value={startingGems}
                  onChange={(e) => setStartingGems(parseInt(e.target.value) || 0)}
                  style={styles.textInput}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Players start with this amount when they first play the game
                </p>
              </div>

              <hr style={styles.divider} />

              {/* Membership Settings */}
              <h3 style={styles.sectionTitle}>🔐 Access Control</h3>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <input 
                    type="checkbox" 
                    checked={requiresMembership}
                    onChange={(e) => setRequiresMembership(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Require Paid Membership
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {requiresMembership 
                    ? '🔒 Only paid members can play this game' 
                    : '🌍 Game is free for all users'}
                </p>
              </div>

              {requiresMembership && (
                <div style={styles.controlGroup}>
                  <label style={styles.label}>Game Price (if standalone):</label>
                  <input 
                    type="number" 
                    min="0" 
                    max="100"
                    value={gamePrice}
                    onChange={(e) => setGamePrice(parseFloat(e.target.value) || 0)}
                    style={styles.textInput}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    If users can buy this game separately (0 = membership only)
                  </p>
                </div>
              )}

              <hr style={styles.divider} />

              <div style={styles.controlGroup}>
                <label style={styles.uploadLabel}>🎨 Background Zoom</label>
                <input 
                  type="range" 
                  min="0.5" 
                  max="2" 
                  step="0.1"
                  value={bgZoom}
                  onChange={(e) => setBgZoom(parseFloat(e.target.value))}
                  style={styles.slider}
                />
                <p style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                  Zoom: {bgZoom.toFixed(1)}x
                </p>
              </div>

              <div style={styles.instructionsBox}>
                <h3 style={styles.instructionsTitle}>💡 Tips:</h3>
                <ul style={styles.instructionsList}>
                  <li>Use Elements tab to position characters and taxi</li>
                  <li>Use Conversations tab to build dialogues</li>
                  <li>Click character buttons at top to quick-access conversations</li>
                  <li>Each character can have multiple dialogue nodes</li>
                  <li><strong>Always click "Save All Configuration" before testing in game!</strong></li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    textAlign: 'center',
    color: 'white',
    fontSize: '32px',
    margin: 0,
    flex: 1,
  },
  toggleMarkersBtn: {
    padding: '10px 15px',
    fontSize: '14px',
    fontWeight: 'bold',
    border: '2px solid white',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  toggleMarkersBtnVisible: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
  },
  toggleMarkersBtnHidden: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  mainContent: {
    display: 'flex',
    gap: '20px',
    maxWidth: '1800px',
    margin: '0 auto',
  },
  canvasArea: {
    flex: 1,
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    position: 'relative',
  },
  canvas: {
    border: '2px solid #333',
    borderRadius: '10px',
    cursor: 'move',
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  controlPanel: {
    width: '350px',
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: '20px',
    marginBottom: '15px',
    color: '#667eea',
  },
  controlGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333',
  },
  slider: {
    width: '100%',
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    cursor: 'pointer',
  },
  divider: {
    border: 'none',
    borderTop: '2px solid #E0E0E0',
    margin: '25px 0',
  },
  coordinatesBox: {
    background: '#F5F5F5',
    padding: '15px',
    borderRadius: '10px',
    marginTop: '20px',
  },
  coordsTitle: {
    fontSize: '16px',
    marginBottom: '10px',
    color: '#333',
  },
  coordsText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '15px',
    fontFamily: 'monospace',
    lineHeight: '1.6',
  },
  copyButton: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  instructionsBox: {
    background: '#FFF9E6',
    padding: '15px',
    borderRadius: '10px',
    marginTop: '20px',
    border: '2px solid #FFD700',
  },
  instructionsTitle: {
    fontSize: '16px',
    marginBottom: '10px',
    color: '#333',
  },
  instructionsList: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.8',
    paddingLeft: '20px',
  },
  elementSelector: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  selectorBtn: {
    flex: 1,
    padding: '12px',
    fontSize: '16px',
    background: '#E0E0E0',
    border: '2px solid #BDBDBD',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  selectorBtnActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #667eea',
  },
  textInput: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  textArea: {
    width: '100%',
    padding: '10px',
    fontSize: '13px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    marginBottom: '10px',
    fontFamily: 'Arial, sans-serif',
    resize: 'vertical',
  },
  markerNotePreview: {
    fontSize: '11px',
    color: '#2196F3',
    marginTop: '4px',
    fontStyle: 'italic',
  },
  addMarkerBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  markersList: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '15px',
  },
  emptyMarkers: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
    fontSize: '14px',
  },
  markerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#F5F5F5',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  markerNumber: {
    width: '30px',
    height: '30px',
    background: '#667eea',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  markerInfo: {
    flex: 1,
  },
  markerNameText: {
    fontWeight: 'bold',
    fontSize: '13px',
    color: '#333',
  },
  markerCoords: {
    fontSize: '11px',
    color: '#666',
    fontFamily: 'monospace',
  },
  markerGoBtn: {
    width: '32px',
    height: '32px',
    background: '#4CAF50',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  markerDeleteBtn: {
    width: '32px',
    height: '32px',
    background: '#F44336',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  markerActions: {
    display: 'flex',
    gap: '8px',
    marginBottom: '15px',
  },
  actionBtn: {
    flex: 1,
    padding: '10px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  clearBtn: {
    flex: 1,
    padding: '10px',
    background: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  axisHint: {
    fontSize: '11px',
    color: '#666',
    marginTop: '5px',
  },
  playControls: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px',
  },
  playBtn: {
    flex: 2,
    padding: '15px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
  },
  stopBtn: {
    flex: 2,
    padding: '15px',
    background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(255, 152, 0, 0.4)',
  },
  resetBtn: {
    flex: 1,
    padding: '15px',
    background: '#9E9E9E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  animationStatus: {
    background: '#E8F5E9',
    border: '2px solid #4CAF50',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#2E7D32',
    marginBottom: '15px',
  },
  styleSelector: {
    display: 'flex',
    gap: '8px',
  },
  styleBtn: {
    flex: 1,
    padding: '10px',
    background: '#F5F5F5',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  styleBtnActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #667eea',
  },
  speedLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '5px',
  },
  speedLabel: {
    fontSize: '11px',
    color: '#999',
  },
  flipControls: {
    display: 'flex',
    gap: '10px',
    marginTop: '8px',
  },
  flipBtn: {
    flex: 1,
    padding: '12px',
    background: '#F5F5F5',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  flipBtnActive: {
    background: '#FF9800',
    color: 'white',
    border: '2px solid #F57C00',
    transform: 'scale(1.05)',
  },
  uploadLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333',
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    fontSize: '13px',
    border: '2px dashed #667eea',
    borderRadius: '8px',
    cursor: 'pointer',
    background: '#F5F5FF',
  },
  uploadedInfo: {
    marginTop: '8px',
    padding: '8px',
    background: '#E8F5E9',
    border: '2px solid #4CAF50',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#2E7D32',
    textAlign: 'center',
  },
  resetImagesBtn: {
    width: '100%',
    padding: '12px',
    marginTop: '15px',
    background: '#9E9E9E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  toggleOverlayBtn: {
    width: '100%',
    padding: '12px',
    background: '#F5F5F5',
    color: '#333',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  toggleOverlayBtnActive: {
    background: '#4CAF50',
    color: 'white',
    border: '2px solid #388E3C',
  },
  autoRotateToggle: {
    width: '100%',
    padding: '15px',
    fontSize: '16px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  autoRotateToggleOn: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
  },
  autoRotateToggleOff: {
    background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(255, 152, 0, 0.4)',
  },
  rotationHint: {
    marginTop: '10px',
    padding: '10px',
    background: '#E3F2FD',
    border: '2px solid #2196F3',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#1565C0',
    textAlign: 'center',
  },
  // Main Tabs
  mainTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '15px',
  },
  mainTabBtn: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    background: '#F5F5F5',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  mainTabBtnActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #667eea',
  },
  // Character Interaction Bar
  characterInteractionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '10px',
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '8px',
    margin: '0 10px',
  },
  charInteractionBtn: {
    padding: '8px 12px',
    fontSize: '13px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  // Gender Selection
  genderSelector: {
    display: 'flex',
    gap: '10px',
    marginTop: '10px',
  },
  genderBtn: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    background: '#F5F5F5',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  genderBtnActive: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    color: 'white',
    border: '2px solid #4CAF50',
  },
  // Character Tabs
  characterTabs: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  characterTab: {
    padding: '12px',
    minWidth: '80px',
    fontSize: '24px',
    background: '#F5F5F5',
    border: '2px solid #E0E0E0',
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s',
  },
  characterTabActive: {
    background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    color: 'white',
    border: '2px solid #FF6B6B',
    transform: 'scale(1.05)',
  },
  // Conversation Builder
  conversationBuilder: {
    background: '#FAFAFA',
    padding: '15px',
    borderRadius: '10px',
    border: '2px solid #E0E0E0',
  },
  conversationList: {
    maxHeight: '300px',
    overflowY: 'auto',
    marginBottom: '15px',
  },
  conversationNode: {
    background: 'white',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '10px',
    border: '2px solid #E0E0E0',
  },
  nodeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#667eea',
  },
  nodeContent: {
    fontSize: '13px',
  },
  deleteNodeBtn: {
    background: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  addNodeSection: {
    background: 'white',
    padding: '12px',
    borderRadius: '8px',
    border: '2px dashed #667eea',
  },
  addNodeBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  emptyState: {
    padding: '30px',
    textAlign: 'center',
    color: '#999',
  },
  saveConfigBtn: {
    width: '100%',
    padding: '15px 20px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
    transition: 'all 0.2s',
  },
  quickSaveBtn: {
    width: '100%',
    padding: '12px 18px',
    background: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 3px 12px rgba(33, 150, 243, 0.3)',
    transition: 'all 0.2s',
    marginBottom: '15px',
  },
  gemTypeBtn: {
    flex: 1,
    padding: '15px 10px',
    textAlign: 'center',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  gemListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    background: '#F5F5F5',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  nodeActionBtn: {
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  // Preview Styles
  previewContainer: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    borderRadius: '15px',
    minHeight: '450px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  // Game-style dialogue layout
  gameDialogueContainer: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  characterPortrait: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '140px',
  },
  dialogueBubbleContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  dialogueBubble: {
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '18px 24px',
    borderRadius: '20px',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    position: 'relative',
    maxWidth: '100%',
  },
  responseOptionsContainer: {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '20px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)',
  },
  gameResponseBtn: {
    width: '100%',
    padding: '15px 20px',
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#333',
    border: '3px solid #FFB74D',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'left',
    transition: 'all 0.2s',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  },
  avatarSelectionCard: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '30px',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    minWidth: '180px',
    color: '#333',
    boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
  },
  // Canvas Overlay Styles
  canvasOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  overlayContent: {
    textAlign: 'center',
    padding: '40px',
  },
  canvasAvatarCard: {
    position: 'relative',
    background: 'white',
    padding: '20px',
    borderRadius: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    minWidth: '150px',
    color: '#333',
  },
  checkmark: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: '#4CAF50',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  startConversationBtn: {
    padding: '15px 40px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '18px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  closeOverlayBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: '2px solid white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  gameDialogueOverlay: {
    width: '90%',
    maxWidth: '900px',
    height: '80%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  dialogueTop: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '30px',
    borderRadius: '20px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
  },
  canvasCharacterPortrait: {
    background: 'rgba(103, 126, 234, 0.1)',
    borderRadius: '50%',
    width: '120px',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '4px solid #667eea',
    flexShrink: 0,
  },
  canvasDialogueBubble: {
    flex: 1,
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#333',
  },
  canvasResponsesBottom: {
    background: 'rgba(0, 0, 0, 0.4)',
    padding: '25px',
    borderRadius: '20px',
    backdropFilter: 'blur(10px)',
  },
  canvasResponseBtn: {
    width: '100%',
    padding: '15px 20px',
    background: 'white',
    color: '#333',
    border: '3px solid #FFB74D',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    textAlign: 'left',
    transition: 'all 0.2s',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
  },
};

export default GameEditorDemo;
